'use client';

import { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faCircleInfo, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  type?: 'danger' | 'warning' | 'info';
  error?: string | null;
}

export default function ConfirmationModal({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  type = 'danger',
  error
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  const getTypeStyles = (): { icon: ReactNode; confirmBg: string; confirmHover: string } => {
    switch (type) {
      case 'danger':
        return {
          icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
          confirmBg: '#ef4444',
          confirmHover: '#dc2626'
        };
      case 'warning':
        return {
          icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
          confirmBg: '#f59e0b',
          confirmHover: '#d97706'
        };
      case 'info':
        return {
          icon: <FontAwesomeIcon icon={faCircleInfo} />,
          confirmBg: 'var(--accent)',
          confirmHover: 'var(--accent)'
        };
      default:
        return {
          icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
          confirmBg: '#ef4444',
          confirmHover: '#dc2626'
        };
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(0, 0, 0, 0.7)' }}
        onClick={!isLoading ? onCancel : undefined}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 p-6 rounded-2xl shadow-2xl"
        style={{
          background: 'var(--gradient-card)',
          border: '1px solid var(--border)'
        }}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-2xl"
            style={{
              background: `${typeStyles.confirmBg}20`,
              color: typeStyles.confirmBg
            }}
          >
            {typeStyles.icon}
          </div>
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h3>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            {message}
          </p>
          {error && (
            <div className="mt-4 p-3 rounded-lg text-sm text-left bg-red-900/30 border border-red-800 text-red-200">
              <p className="font-bold mb-1">Error:</p>
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50"
            style={{
              background: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)'
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 hover:opacity-90"
            style={{
              background: typeStyles.confirmBg,
              color: 'white'
            }}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <span className="animate-spin mr-2">
                  <FontAwesomeIcon icon={faSpinner} />
                </span>
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
